import React from "react";
import { Switch } from "@/components/ui/switch";
import {
  SortableServiceList,
  SortableServiceItem,
} from "@/components/common/SortableServiceList";
import { HotelService } from "@/types/hotelService";
import { CheckinSettingsResponse } from "@/types/booking-checkin-settings";

const Label = ({ children, className }: any) => (
  <label className={`text-2xl font-medium ${className || ""}`}>
    {children}
  </label>
);

interface BookingInfoTabProps {
  settings: CheckinSettingsResponse | null;
  setSettings: React.Dispatch<
    React.SetStateAction<CheckinSettingsResponse | null>
  >;
  upsellServices: SortableServiceItem[];
  setUpsellServices: (services: SortableServiceItem[]) => void;
  allServices: HotelService[];
}

export const BookingInfoTab = ({
  settings,
  setSettings,
  upsellServices,
  setUpsellServices,
  allServices,
}: BookingInfoTabProps) => {
  const updateSetting = (
    key: keyof CheckinSettingsResponse,
    value: CheckinSettingsResponse[keyof CheckinSettingsResponse],
  ) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            [key]: value,
          }
        : previousSettings,
    );
  };

  return (
    <div className="space-y-6">
      {/* Show Room Category Photos */}
      <div className="flex gap-2 items-center">
        <Switch
          checked={settings?.isShowRoomCategoryPhotos}
          onCheckedChange={(checked) =>
            updateSetting("isShowRoomCategoryPhotos", checked)
          }
        />
        <Label>Show room category photos</Label>
      </div>

      {/* Additional Services Section */}
      <div className="space-y-4">
        <div className="flex gap-2 items-center">
          <Switch
            checked={settings?.isShowAdditionalServices}
            onCheckedChange={(checked) =>
              updateSetting("isShowAdditionalServices", checked)
            }
          />
          <Label>Additional services</Label>
        </div>

        {settings?.isShowAdditionalServices && (
          <>
            {/* Selected Services List */}
            <div className="ml-6 space-y-2">
              <SortableServiceList
                items={upsellServices}
                onReorder={setUpsellServices}
                onRemove={(id) =>
                  setUpsellServices(upsellServices.filter((s) => s.id !== id))
                }
                onAdd={(serviceId) => {
                  const service = allServices.find((s) => s.id === serviceId);
                  if (service) {
                    setUpsellServices([
                      ...upsellServices,
                      {
                        id: service.id,
                        serviceId: service.id,
                        name: service.name,
                      },
                    ]);
                  }
                }}
                availableServices={allServices}
                placeholder="Choose service to make it available for checkin"
              />
            </div>
          </>
        )}
      </div>
      {/* Comments/Preferences/Requests */}
      <div className="flex gap-2 items-center">
        <Switch
          checked={settings?.isShowCommentsPreferencesRequests}
          onCheckedChange={(checked) =>
            updateSetting("isShowCommentsPreferencesRequests", checked)
          }
        />

        <Label>Comments/Preferences/Requests</Label>
      </div>
    </div>
  );
};
